import { NOTIFICATION_TIERS, type AnalyticsDashboardResponse } from '@clm/shared-types';

import { bulkIngestAffordance } from '../../auth/permissions';
import { useIdentity } from '../../auth/useIdentity';
import { StatTile } from '../../components/charts/StatTile';
import { ATTENTION_CAPTION, DUE_PANEL_ID, TIER_LABELS, panelTriggerId, samePanel, type DashboardPanel } from './labels';

/**
 * The **Needs attention** strip at the top of the Dashboard — CLM-90 (D-90-2).
 *
 * Tiles, in order: *Due < 30 / 60 / 90 d* and *Past due* — each toggles the
 * due-for-renewal list open in place, filtered to that window (CLM-95,
 * D-95-1/D-95-3; no longer a navigating link) — one tile per notification
 * tier reading sent / acknowledged / not sent for the < 90 d set
 * (→ Notifications), *Fields pending verification* (→ the verification queue)
 * and *Extraction failed* (→ Pipeline, Contributor or Admin only —
 * `bulkIngestAffordance`, the `NavRail` rule, widened by R-94-1 / CLM-94).
 * Every count is the API's, in the caller's scope; no
 * fused "attention score" — the `NeedsAttention` reasoning stands: no
 * browser-invented precedence between a date and an unverified field. No LLM.
 *
 * Sole owner of its hook (`useIdentity`), so `AnalyticsDashboard.tsx` mounts
 * it with one import line and one JSX line (MERGE-ORDER, the CLM-91 seam).
 * CLM-95 — the due tiles' toggle state is NOT this component's own: the panel
 * that spans due/KPI/status-mix groups is owned by `AnalyticsDashboard.tsx`
 * (D-95-3), passed down as `activePanel` + `onToggleDue`.
 */

export function AttentionStrip({
  attention,
  kpis,
  statusMix,
  activePanel,
  onToggleDue,
}: {
  readonly attention: AnalyticsDashboardResponse['attention'];
  readonly kpis: AnalyticsDashboardResponse['kpis'];
  readonly statusMix: AnalyticsDashboardResponse['status_mix'];
  readonly activePanel: DashboardPanel | null;
  readonly onToggleDue: (window: DashboardPanel & { readonly kind: 'due' }) => void;
}) {
  const { identity, isAuthenticated } = useIdentity();
  const canOpenPipeline = bulkIngestAffordance(identity, isAuthenticated) === 'eligible';
  const failed = statusMix.counts.ExtractionFailed;

  return (
    <section className="card analytics-section attention" aria-labelledby="attention-heading">
      <h3 id="attention-heading">Needs attention</h3>
      <div className="kpi-grid" role="group" aria-label="Needs attention">
        <StatTile
          title="Due < 30 d"
          n={attention.due_30}
          tone="acc"
          id={panelTriggerId({ kind: 'due', window: '30' })}
          expanded={samePanel(activePanel, { kind: 'due', window: '30' })}
          panelId={DUE_PANEL_ID}
          onToggle={() => {
            onToggleDue({ kind: 'due', window: '30' });
          }}
        />
        <StatTile
          title="Due < 60 d"
          n={attention.due_60}
          tone="acc"
          id={panelTriggerId({ kind: 'due', window: '60' })}
          expanded={samePanel(activePanel, { kind: 'due', window: '60' })}
          panelId={DUE_PANEL_ID}
          onToggle={() => {
            onToggleDue({ kind: 'due', window: '60' });
          }}
        />
        <StatTile
          title="Due < 90 d"
          n={attention.due_90}
          tone="acc"
          id={panelTriggerId({ kind: 'due', window: '90' })}
          expanded={samePanel(activePanel, { kind: 'due', window: '90' })}
          panelId={DUE_PANEL_ID}
          onToggle={() => {
            onToggleDue({ kind: 'due', window: '90' });
          }}
        />
        <StatTile
          title="Past due"
          n={attention.past_due}
          tone={attention.past_due > 0 ? 'bad' : undefined}
          id={panelTriggerId({ kind: 'due', window: 'past_due' })}
          expanded={samePanel(activePanel, { kind: 'due', window: 'past_due' })}
          panelId={DUE_PANEL_ID}
          onToggle={() => {
            onToggleDue({ kind: 'due', window: 'past_due' });
          }}
        />
        {NOTIFICATION_TIERS.map((tier) => {
          const counts = attention.by_tier[tier];
          return (
            <StatTile
              key={tier}
              title={TIER_LABELS[tier]}
              n={counts.sent + counts.acknowledged}
              unit="contracts"
              caption={`${String(counts.acknowledged)} acknowledged · ${String(counts.not_sent)} not sent`}
              tone={counts.not_sent > 0 ? 'bad' : undefined}
              to="/governance/notifications"
            />
          );
        })}
        <StatTile title="Fields pending verification" n={kpis.fields_pending} unit="fields" tone="acc" to="/governance/verification" />
        <StatTile
          title="Extraction failed"
          n={failed}
          unit="contracts"
          tone={failed > 0 ? 'bad' : undefined}
          to={canOpenPipeline ? '/admin/pipeline' : undefined}
        />
      </div>
      <p className="mut">{ATTENTION_CAPTION}</p>
    </section>
  );
}
